import { Popover } from "antd";
import {
  getPlaceholderForEmptyString,
  hasAnyEmptyField,
  isEmptyValue,
} from "../../utils/emptyStringCheck";
import { PopoverTrigger } from "../../enums/PopoverEnums";
import styles from "./CustomerDatabaseList.module.scss";
import MoreEllipsis from "../../assets/images/ellipsisListing.svg";
import EditIcon from "../../assets/images/edit-document-icon.svg";
import ArchiveIcon from "../../assets/images/make-primary-document-icon.svg";
import * as Yup from "yup";
import { CustomerFilterParams } from "../../Types/customerDatabaseTypes";
import { TableSortTypes } from "../../enums/ViewListingTypes";
import { regexPatterns } from "../../utils/regexUtil";
import DescriptionCell from "../../reusable-components/DescriptionCell";
import { getTableSortOrder } from "../../utils/getTableSortOrder";

export const CustomerSortColumns = {
  COMPANY_NAME: "companyName",
  TOTAL_JOBS: "totalJobs",
  ACTIVE_JOBS: "activeJobs",
  OVERDUE_INVOICES: "overdueInvoices",
  CURRENT_BALANCE: "currentBalance",
  OVERDUE_BALANCE: "overdueBalance",
  LIFETIME_REVENUE: "revenue",
  COGS: "cogs",
  GROSS_PROFIT: "grossProfit",
  GROSS_PROFIT_PERCENTAGE: "grossProfitPercentage",
};


export const customerDatabaseColumns = ({
  handleEditAction,
  handleArchiveAction,
  filterValues,
}: {
  handleEditAction: (record?: any) => void;
  handleArchiveAction: (record?: any) => void;
  filterValues: CustomerFilterParams;
}) => {
  const { sortBy, sortOrder } = filterValues || {};

  return [
    {
      title: "CUSTOMER NAME",
      dataIndex: "companyName",
      key: CustomerSortColumns.COMPANY_NAME,
      width: "20rem",
      align: "center",
      sorter: true,
      sortOrder: getTableSortOrder(
        sortBy,
        sortOrder,
        CustomerSortColumns.COMPANY_NAME,
      ),
      render: (companyName) => (
        <span className={styles.customerTableCustomerName}>
          <DescriptionCell text={getPlaceholderForEmptyString(companyName)} />
        </span>
      ),
    },
    {
      title: "TOTAL JOBS",
      dataIndex: "totalJobs",
      key: CustomerSortColumns.TOTAL_JOBS,
      width: "25px",
      align: "center",
      sorter: true,
      sortOrder: getTableSortOrder(
        sortBy,
        sortOrder,
        CustomerSortColumns.TOTAL_JOBS,
      ),
      render: (totalJobs) => (
        <>{hasAnyEmptyField(totalJobs) ? "0" : totalJobs}</>
      ),
    },
    {
      title: "ACTIVE JOBS",
      dataIndex: "activeJobs",
      key: CustomerSortColumns.ACTIVE_JOBS,
      width: "25px",
      align: "center",
      sorter: true,
      sortOrder: getTableSortOrder(
        sortBy,
        sortOrder,
        CustomerSortColumns.ACTIVE_JOBS,
      ),
      render: (activeJobs) => (
        <>{hasAnyEmptyField(activeJobs) ? 0 : activeJobs}</>
      ),
    },
    {
      title: "OVERDUE INVOICES",
      dataIndex: "overdueInvoices",
      key: CustomerSortColumns.OVERDUE_INVOICES,
      width: "25px",
      align: "center",
      sorter: true,
      sortOrder: getTableSortOrder(
        sortBy,
        sortOrder,
        CustomerSortColumns.OVERDUE_INVOICES,
      ),
      render: (overdueInvoices) => (
        <>{hasAnyEmptyField(overdueInvoices) ? 0 : overdueInvoices}</>
      ),
    },
    {
      title: "CURRENT BALANCE",
      dataIndex: "currentBalance",
      key: CustomerSortColumns.CURRENT_BALANCE,
      width: "40px",
      align: "center",
      sorter: true,
      sortOrder: getTableSortOrder(
        sortBy,
        sortOrder,
        CustomerSortColumns.CURRENT_BALANCE,
      ),
      render: (currentBalance) => (
        <>{getPlaceholderForEmptyString(currentBalance)}</>
      ),
    },
    {
      title: "OVERDUE BALANCE",
      dataIndex: "overdueBalance",
      key: CustomerSortColumns.OVERDUE_BALANCE,
      width: "40px",
      align: "center",
      sorter: true,
      sortOrder: getTableSortOrder(
        sortBy,
        sortOrder,
        CustomerSortColumns.OVERDUE_BALANCE,
      ),
      render: (overdueBalance) => (
        <>{getPlaceholderForEmptyString(overdueBalance)}</>
      ),
    },
    {
      title: "LIFETIME REVENUE",
      dataIndex: "revenue",
      key: CustomerSortColumns.LIFETIME_REVENUE,
      width: "40px",
      align: "center",
      sorter: true,
      sortOrder: getTableSortOrder(
        sortBy,
        sortOrder,
        CustomerSortColumns.LIFETIME_REVENUE,
      ),
      render: (revenue) => <>{getPlaceholderForEmptyString(revenue)}</>,
    },
    {
      title: "COGS",
      dataIndex: "cogs",
      key: CustomerSortColumns.COGS,
      width: "40px",
      align: "center",
      sorter: true,
      sortOrder: getTableSortOrder(sortBy, sortOrder, CustomerSortColumns.COGS),
      render: (cogs) => <>{getPlaceholderForEmptyString(cogs)}</>,
    },
    {
      title: "GROSS PROFIT",
      dataIndex: "grossProfit",
      key: CustomerSortColumns.GROSS_PROFIT,
      width: "40px",
      align: "center",
      sorter: true,
      sortOrder: getTableSortOrder(
        sortBy,
        sortOrder,
        CustomerSortColumns.GROSS_PROFIT,
      ),
      render: (grossProfit) => <>{getPlaceholderForEmptyString(grossProfit)}</>,
    },
    {
      title: "GP%",
      dataIndex: "grossProfitPercentage",
      key: CustomerSortColumns.GROSS_PROFIT_PERCENTAGE,
      width: "40px",
      align: "center",
      sorter: true,
      sortOrder: getTableSortOrder(
        sortBy,
        sortOrder,
        CustomerSortColumns.GROSS_PROFIT_PERCENTAGE,
      ),
      render: (grossProfitPercentage) => (
        <>
          {isEmptyValue(grossProfitPercentage)
            ? ""
            : `${Number(grossProfitPercentage).toFixed(2)}%`}
        </>
      ),
    },
    {
      dataIndex: "actions",
      key: "actions",
      width: "40px",
      align: "center",
      fixed:"right",
      render: (_, record) => (
        <Popover
          trigger={PopoverTrigger.HOVER}
          placement="leftTop"
          content={
            <div className={styles.actionsContainer}>
              <span onClick={() => handleEditAction(record)}>
                <img src={EditIcon} />
                <span className={styles.actionOptionText}>Edit</span>
              </span>
              <span onClick={() => handleArchiveAction(record)}>
                <img src={ArchiveIcon} />
                <span className={styles.actionOptionText}>Archive</span>
              </span>
            </div>
          }
        >
          <span className={styles.editEllipsis}>
            <img src={MoreEllipsis} />
          </span>
        </Popover>
      ),
    },
  ];
};

export const mockDataObj = {
  id: "sdssdcsq2e12312",
  companyName: "Twisted Nail",
  totalJobs: 3,
  activeJobs: 11,
  overdueInvoices: "8",
  currentBalance: "",
  overdueBalance: "",
  revenue: "$3,395.00",
  cogs: "$3,395.00",
  grossProfit: "$370.00",
  grossProfitPercentage: 12.23,
};

export const NO_CUSTOMER_LIST_MESSAGE = "No Records Present";

export const archiveFormSchema = Yup.object({
  archiveReason: Yup.string()
    .trim()
    .required("Reason required for archive action"),
});

export const addCustomerFormSchema = Yup.object()
  .shape({
    customerName: Yup.string().required("This is a required field"),
    addressField: Yup.string().notRequired(),
    addressData: Yup.object().notRequired(),
    emailAddress: Yup.string()
      .transform((value) => value?.trim() || undefined)
      .notRequired()
      .matches(regexPatterns.emailRegex, "Please enter valid email address"),
    officePhone: Yup.string()
      .notRequired()
      .test(
        "valid-phone",
        "Please enter a valid 10-digit phone number",
        (value) => {
          if (!value) return true;
          const digits = value.replace(/[^\d]/g, "");
          return digits.length === 10;
        },
      ),
    accountTerms: Yup.string().notRequired(),
    customTerms: Yup.string().when("accountTerms", {
      is: (val: string) => val === "Custom Input",
      then: () => Yup.string().required("This is a required for custom terms"),
      otherwise: () => Yup.string().notRequired(),
    }),
    accountLink: Yup.string().notRequired(),
    notesField: Yup.string().notRequired(),
  })
  .test(
    "one-contact-method",
    "Either email or phone number is required",
    function (value) {
      const { emailAddress, officePhone } = value || {};
      if (emailAddress || officePhone) {
        return true;
      }
      return this.createError({
        path: "emailAddress",
        message: "Either email or phone number is required",
      });
    },
  );

export const filterDrawerSchema = Yup.object()
  .shape({
    searchField: Yup.string().notRequired(),
    addressField: Yup.string().notRequired(),
    addressData: Yup.object()
      .shape({
        lat: Yup.string().notRequired(),
        lng: Yup.string().notRequired(),
      })
      .notRequired(),
    radiusField: Yup.string().notRequired(),
  })
  .test(
    "radius-requires-location",
    "Address and coordinates are required when radius is set",
    function (value) {
      const { radiusField, addressField, addressData } = value || {};
      if (!radiusField) {
        return true;
      }
      if (
        addressField &&
        addressData != null &&
        addressData.lat &&
        addressData.lng
      ) {
        return true;
      }
      return this.createError({
        path: "addressField",
        message: "Address is required",
      });
    },
  );
